import { HTMLAttributes } from 'react'
import { cn } from '~utils/cn'

import dayjs from 'dayjs'
import InlineProfile from '~components/general/inline-profile'
import PetCard from '~components/general/pet-card'
import Button from '~components/ui/button'
import Icon from '~components/ui/icon'
import SanitizedText from '~components/ui/sanitized-text'
import { AVATAR_DEFAULT } from '~constants'
import Loading from '~containers/general/loading'
import useConsultationDetail from '~repositories/hooks/use-consultation-detail'
import Conversation from './conversation'
import { IssueDetailsSlider } from './issue-details'
import VetAnswer from './vet-answer'

type ConsultationContentProps = Readonly<
  HTMLAttributes<HTMLDivElement> & {
    consultationId: number
  }
>

const Divider = () => (
  <hr className="-mx-6 h-[1px] border-gray-300 lg:hidden dark:border-gray-700" />
)

const Error = ({ onRetry }: { onRetry: () => void }) => {
  return (
    <div className="flex h-full flex-col items-center justify-center flex-gap-y-4 px-5 py-6 lg:h-[524px]">
      <p className="max-w-[312px] whitespace-pre-line text-center text-primary-600 text-body-2">
        {`We could not load the content due to issues on our end.\nPlease try reloading the content.`}
      </p>
      <Button priority="secondary" className="flex items-center flex-gap-x-2" onClick={onRetry}>
        Reload
        <Icon name="recursion_sm" className="size-6 fill-primary-600" />
      </Button>
    </div>
  )
}

export const ConsultationContent = ({ consultationId, ...props }: ConsultationContentProps) => {
  const {
    data: consultation,
    isFetching,
    isError,
    refetch,
  } = useConsultationDetail({
    id: consultationId,
    staleTime: 20 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })

  const owner = consultation?.owner
  const ownerInfo = {
    thumbnailUri: owner?.thumbnail_uri || AVATAR_DEFAULT.OWNER,
    nickname: owner?.nickname || 'Unknown',
  }

  const initialAnswer = consultation?.conversation.at(1)
  const conversation = consultation?.conversation.slice(2) // 질문 본문 및 첫 답변 제외

  const vet = consultation?.vet
  const vetInfo = {
    thumbnailUri: vet?.photo_url || '',
    nickname: vet?.title || 'Dr.Tail Vet',
  }
  const checkupSummary = consultation?.checkup_summary || {}

  const pet = consultation?.pet

  if (isFetching) {
    return <Loading />
  }

  if (isError) {
    return <Error onRetry={refetch} />
  }

  return (
    <div className={cn('overflow-x-hidden overflow-y-scroll px-6 py-5', props.className)}>
      <InlineProfile size="lg" className="py-2">
        <InlineProfile.Avatar thumbnailUri={ownerInfo.thumbnailUri} />
        <InlineProfile.Header
          nickname={ownerInfo.nickname}
          createdAt={dayjs(consultation?.created_at).format('MMM DD, YYYY')}
        />
      </InlineProfile>
      {consultation?.details && (
        <p className="py-2 text-gray-900 text-body-2 lg:text-body-3 dark:text-gray-300">
          <SanitizedText text={consultation.details} />
        </p>
      )}
      {pet && (
        <div className="my-6">
          <PetCard pet={pet} className="flex flex-gap-x-3 bg-extended-alabaster dark:bg-gray-800">
            <PetCard.Avatar />
            <div className="min-w-0">
              <PetCard.Name />
              <PetCard.Kind />
              <PetCard.AgeWithGender />
            </div>
          </PetCard>
        </div>
      )}
      {!!Object.keys(checkupSummary)?.length && (
        <div className="py-3">
          <IssueDetailsSlider checkupSummary={checkupSummary} />
        </div>
      )}
      <Divider />
      {initialAnswer && vet && (
        <div className="-mx-5">
          <VetAnswer vet={vet} answer={initialAnswer.text} createdAt={initialAnswer.created_at} />
        </div>
      )}
      {!!conversation?.length && (
        <div className="-mx-5">
          <Conversation conversation={conversation} ownerInfo={ownerInfo} vetInfo={vetInfo} />
        </div>
      )}
    </div>
  )
}
